import { updateUserV1 } from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, APIResponse, User } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { getCurrentUserOptions } from "./firm-user";

export type DashboardPreferenceKey =
  | "dashboardNotifications"
  | "dashboardTasks"
  | "dashboardMyApproachingStatuteOfLimitationMatters"
  | "dashboardFavoriteMatters"
  | "dashboardMostRecentMatters";

type UpdateDashboardPreference = Pick<User, "id" | "dateModified"> & {
  key: DashboardPreferenceKey;
  checked: boolean;
};

export function useUpdateDashboardPreference() {
  const queryClient = useQueryClient();
  const queryKey = getCurrentUserOptions().queryKey;

  return useMutation({
    mutationFn: ({
      id,
      dateModified,
      key,
      checked,
    }: UpdateDashboardPreference) =>
      updateUserV1({ id, dateModified, [key]: checked }),
    onMutate: () => queryClient.cancelQueries({ queryKey, exact: true }),
    onSuccess: (response) => {
      // The saved user includes the new version for the next toggle. Dashboard
      // visibility does not affect directory entries, search, or permissions.
      queryClient.setQueryData<APIResponse<User>>(queryKey, (oldData) => {
        if (!oldData || oldData.data.id !== response.data.id) {
          return oldData;
        }

        return {
          ...oldData,
          data: { ...oldData.data, ...response.data },
        };
      });
      // Settings rows also carry the user version. Refresh them when reopened
      // without starting another request or holding up dashboard switches.
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.users, "settings"],
        refetchType: "none",
      });
    },
    onError: async (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating dashboard preferences");
      await queryClient.invalidateQueries({ queryKey, exact: true });
    },
  });
}
